import { Item, ItemWithMetaData } from "../api/Items";
import { SocialIcon } from "../utils/social-icon";
import Button, { ButtonColorTypes } from "../shared-components/Button";
import Link from "../shared-components/icons/Link";
import { formattedDate } from "../utils";
import {
  useCreateItemPostRating,
  useRemovePostsItem,
  useRestorePostItemRatings,
} from "../api/ItemsApi";
import { useEffect, useState } from "react";
import { HARDCODED_ACTOR_UUID } from "../api/helpers";
import {
  contentMediaToMediaExternalInternal,
  MediaExternalInternalURLs,
  PostImage,
} from "./PostImage";
import { useReportTimer } from "../reports/ReportDetailProvider";
import { behaviorMap, useFamaFlags } from "../api/FamaFlags";
import PostArticleTranslation from "./PostArticleTranslation";
import { getDisplayPostTypeText } from "../utils/post-utils";
import ChipGroup from "../shared-components/ChipGroup";
import { useApplicationToast } from "../webcontent/ApplicationToastProvider";
import { PostRatingBehavior } from "../utils/types";

export type PostRateMediaAction = "submit" | "remove" | "undo" | "removeFailed";

const UNDO_TOAST_DURATION_MS = 2000;

type PostRateMediaRatingProps = {
  behaviors: string[];
  currentPost: ItemWithMetaData;
  isReadOnly: boolean;
  handleAction: (action: PostRateMediaAction, post: Item) => void;
  beginOperation: () => number;
  endOperation: (token: number) => void;
};

const PostRateMediaRating = ({
  behaviors,
  currentPost,
  isReadOnly,
  handleAction,
  beginOperation,
  endOperation,
}: PostRateMediaRatingProps) => {
  const { mutateAsync: removeItemAsync } = useRemovePostsItem();
  const { mutateAsync: restoreItemAsync } = useRestorePostItemRatings();
  const { mutateAsync: createItemRatingAsync } = useCreateItemPostRating();

  const { behaviorMappingActions } = useFamaFlags();
  const { showPushToast } = useApplicationToast();

  const handleSubmit = (selected: string[]) => {
    const submittedItem = currentPost.item;
    const ratings: PostRatingBehavior[] = selected.map((behavior) => ({
      name: behaviorMap(behavior, behaviorMappingActions, "value"),
      score_type: "behavior",
    }));

    const token = beginOperation();
    handleAction("submit", submittedItem);

    void createItemRatingAsync({
      item_uuid: submittedItem.external_id,
      ratings: ratings,
    })
      .catch(() => {
        handleAction("undo", submittedItem);
        showPushToast?.({
          message: "Failed to submit post rating. It has been restored.",
          type: "error",
          autoDismiss: true,
          duration: UNDO_TOAST_DURATION_MS,
        });
      })
      .finally(() => endOperation(token));
  };

  const handleRemove = () => {
    const removedItem = currentPost.item;
    const removeInput = {
      item_uuid: removedItem.external_id,
      modified_by: HARDCODED_ACTOR_UUID, // TODO: remove once the actor uuid definition is complete
    };

    // Hold pending through the undo toast window so PostRate cannot load-next /
    // complete while Undo can still restore against the current page.
    const token = beginOperation();
    const removal = removeItemAsync(removeInput);
    handleAction("remove", removedItem);

    let undoClicked = false;
    let removeSettled = false;
    let undoWindowClosed = false;
    let ended = false;

    const endIfReady = () => {
      if (ended || undoClicked) return;
      if (removeSettled && undoWindowClosed) {
        ended = true;
        endOperation(token);
      }
    };

    const closeUndoWindow = () => {
      undoWindowClosed = true;
      endIfReady();
    };

    // Safety net if toast callbacks never fire (no provider / replaced oddly).
    // Toast auto-dismiss waits `duration` then ~300ms exit before onDismiss.
    const undoWindowTimer = setTimeout(
      closeUndoWindow,
      UNDO_TOAST_DURATION_MS + 350
    );

    void removal
      .catch(() => {
        if (!undoClicked) {
          clearTimeout(undoWindowTimer);
          handleAction("removeFailed", removedItem);
          showPushToast?.({
            message: "Failed to remove post. It has been restored.",
            type: "error",
            autoDismiss: true,
            duration: UNDO_TOAST_DURATION_MS,
          });
          closeUndoWindow();
        }
      })
      .finally(() => {
        removeSettled = true;
        endIfReady();
      });

    showPushToast?.({
      message: "Post has been removed.",
      actionLabel: "Undo",
      onAction: () => {
        if (undoClicked) return;
        undoClicked = true;
        clearTimeout(undoWindowTimer);

        handleAction("undo", removedItem);

        void (async () => {
          try {
            try {
              await removal;
            } catch {
              // Remove failed; UI already restored via undo. Skip restore API.
              return;
            }
            await restoreItemAsync({
              item_uuid: removedItem.external_id,
              modified_by: HARDCODED_ACTOR_UUID, // TODO: remove once the actor uuid definition is complete
            });
          } catch {
            handleAction("remove", removedItem);
            showPushToast?.({
              message: "Failed to undo post removal.",
              type: "error",
              autoDismiss: true,
              duration: UNDO_TOAST_DURATION_MS,
            });
          } finally {
            if (!ended) {
              ended = true;
              endOperation(token);
            }
          }
        })();
      },
      onDismiss: () => {
        clearTimeout(undoWindowTimer);
        closeUndoWindow();
      },
      onCancel: () => {
        clearTimeout(undoWindowTimer);
        closeUndoWindow();
      },
      type: "neutral",
      autoDismiss: true,
      duration: UNDO_TOAST_DURATION_MS,
    });
  };

  return (
    <div className="border p-6 rounded-lg">
      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2">
          <SocialMediaPost
            key={currentPost.item.external_id}
            post={currentPost}
          />
        </div>
        <div className="col-span-1">
          <PostRating
            key={currentPost.item.external_id}
            PostBehaviorList={behaviors}
            isReadOnly={isReadOnly}
            handleSubmit={handleSubmit}
            handleRemove={handleRemove}
          />
        </div>
      </div>
    </div>
  );
};

type SocialMediaPostProps = {
  post: ItemWithMetaData;
};

export const SocialMediaPost = ({ post }: SocialMediaPostProps) => {
  const postContent: any = post.item.content;
  const postImageUrls: MediaExternalInternalURLs[] =
    contentMediaToMediaExternalInternal(postContent);
  return (
    <div className="rounded-lg">
      <SocialMediaPostHeader
        post={post}
        postUrl={postContent.url}
        profileUrl={postContent.profile_url}
        datePosted={postContent.created_at}
      />
      {postContent.text && (
        <div className="font-normal text-base leading-6 text-gray-600">
          <div className="mt-3">{postContent.text}</div>
          <PostArticleTranslation item={post} />
        </div>
      )}
      <PostImage media={postImageUrls} />
    </div>
  );
};

export const PostRating = ({
  PostBehaviorList,
  isReadOnly,
  handleSubmit,
  handleRemove,
}: {
  PostBehaviorList: string[];
  isReadOnly: boolean;
  handleSubmit: (ratings: string[]) => void;
  handleRemove: () => void;
}) => {
  const [postRatings, setPostRatings] = useState<string[]>([]);
  const [flagList, setFlagList] = useState(PostBehaviorList);
  const { resetTimer } = useReportTimer();
  const [removeError, setRemoveError] = useState<string | null>(null);

  useEffect(() => {
    // Only clear error when user deselects all flags
    if (removeError && postRatings.length === 0) {
      setRemoveError(null);
    }
  }, [postRatings, removeError]);

  useEffect(() => {
    setFlagList(PostBehaviorList);
  }, [PostBehaviorList, setFlagList]);

  function reset() {
    setPostRatings([]);
  }

  function handleOnRemove() {
    if (postRatings.length > 0) {
      setRemoveError(
        "Cannot remove a post that has been flagged. Please deselect the behavior flag(s) and try again."
      );
      return;
    }

    handleRemove();
    reset();
    resetTimer?.();
  }

  function handleOnSubmit() {
    handleSubmit([...postRatings]);
    reset();
    resetTimer?.();
  }

  return (
    <div className="flex flex-col bg-gray-50 rounded-md p-4 w-full justify-between overflow-hidden">
      <div>
        <div className="left-0 top-0 text-xl font-bold leading-7 tracking-normal text-left mb-4">
          Behavior Flag:
        </div>
        <ChipGroup
          options={flagList}
          selected={postRatings}
          onChange={(selected) => setPostRatings(selected)}
          maxWidth="max-w-24"
          disabled={isReadOnly}
        />
      </div>
      {!isReadOnly && (
        <div className="flex flex-col mt-auto">
          <Button
            className="mb-2"
            text={"Submit Flags"}
            onClick={handleOnSubmit}
            disabled={postRatings.length === 0}
          />
          <Button
            text={"Remove Post"}
            onClick={handleOnRemove}
            colorType={ButtonColorTypes.cancel}
          />
          {removeError && (
            <div className="mt-2 text-sm text-center text-gray-600 bg-red-50 border border-red-200 rounded-md p-2">
              {removeError}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

type SocialMediaPostHeaderProps = {
  postUrl: string;
  profileUrl: string;
  datePosted: string;
  post: ItemWithMetaData;
};
const SocialMediaPostHeader = ({
  postUrl,
  profileUrl,
  datePosted,
  post,
}: SocialMediaPostHeaderProps) => {
  function getDisplayDate(datePosted: string): string {
    if (datePosted) {
      return formattedDate(new Date(datePosted));
    }
    return "";
  }
  const displayDate: string = getDisplayDate(datePosted);
  const postTypeText = getDisplayPostTypeText(post);
  return (
    <div className="flex flex-nowrap items-center mb-6">
      <SocialIcon url={profileUrl} />
      <div className="flex flex-col">
        <div className="text-sm leading-5 font-medium mx-3">{`${postTypeText} on ${post.source?.name}`}</div>
        {displayDate && (
          <div className="text-sm leading-5 font-normal mx-3">
            {displayDate}
          </div>
        )}
      </div>
      <a
        href={postUrl}
        target="_blank"
        rel="noreferrer"
        className="flex ml-auto"
      >
        <Link />
        <div className="text-xs leading-4 font-medium px-2">View Link</div>
      </a>
    </div>
  );
};

export default PostRateMediaRating;
